import React, { useEffect, useRef, useState } from "react";
import {
  Table,
  InputNumber,
  Form,
  AutoComplete,
  Button,
  Space,
  message,
  Input,
  Select,
} from "antd";
import { IconGenerate, IconPrint, IconBillSave } from "Common/ReactIcons";
import api from "utils/api";
import utils from "utils";
import AutoCompleteDebounce from "Components/AutoCompleteDebounce";
const BillingTable = ({
  dataSource,
  setDataSource,
  form,
  selectedType,
  handleSubmit,
  buttonLoad,
  editData,
}) => {
  const [itemOptions, setItemOptions] = useState([]);
  const [loading, setLoading] = useState({});
  const [editMode, setEditMode] = useState("");
  const initialDataRef = useRef({});

  const setInitialData = (data) => {
    initialDataRef.current = utils.deepCopy(data);
  };

  useEffect(() => {
    setItemOptions([]);
    setEditMode("");
  }, [selectedType]);

  const fetchItemOptions = async (searchText = "", index) => {
    setLoading((prev) => ({ ...prev, [index]: true }));
    try {
      const query = new URLSearchParams({
        search: searchText,
        type: selectedType.toLowerCase(),
      }).toString();
      const response = await api.fetchNewBillingItemSearch(query);
      // const response = {
      //   status: 200,
      //   message: "success",
      //   data: {
      //     count: 3,
      //     results: [
      //       { item: "Item 1", price: 100 },
      //       { item: "Item 2", price: 200 },
      //       { item: "Item 3", price: 300 },
      //     ],
      //   },
      // };
      if (response.data.status === 200) {
        const options = response.data.data.results.map((result, index) => ({
          value: result.item,
          label: result.item,
          unit_price: result.price,
          key: index,
        }));
        setItemOptions(options);
      } else {
        console.error(`Failed to fetch item options`);
      }
    } catch (error) {
      console.error(`Error fetching item options:`, error);
    } finally {
      setLoading((prev) => ({ ...prev, [index]: false }));
    }
  };
  const hideColumns =
    selectedType == "vendor"
      ? {}
      : { hsn: true, quantity: false, unit_price: false };
  const handleAdd = () => {
    const newData = {
      key: dataSource.length + 1,
      description: "",
      hsn: "",
      quantity: 1,
      unit_measurement: "",
      unit_price: 0,
      price: 0,
      discount: 0,
      amount: 0,
    };
    setDataSource([...dataSource, newData]);
    setEditMode(dataSource.length + 1);
  };
  const handleDelete = (key) => {
    const newDataSource = dataSource
      .filter((item) => item.key !== key)
      .map((item, index) => ({ ...item, key: index + 1 }));
    setDataSource(newDataSource);
    form.setFieldsValue({
      items: newDataSource,
    });
  };
  const handleItemSelect = (index, value, option) => {
    const updatedDataSource = [...dataSource];
    updatedDataSource[index].description = value;
    updatedDataSource[index].unit_price = option.unit_price;
    updatedDataSource[index].quantity = updatedDataSource[index].quantity || 1; // Ensure quantity is defined
    updatedDataSource[index].price =
      updatedDataSource[index].unit_price * updatedDataSource[index].quantity;
    updatedDataSource[index].amount =
      updatedDataSource[index].price -
      (updatedDataSource[index].price *
        (updatedDataSource[index].discount || 0)) /
        100;

    setDataSource(updatedDataSource);

    form.setFieldsValue({
      items: updatedDataSource,
    });
  };

  const handleEdit = (record) => {
    setEditMode(record.key);
    setInitialData(record);
  };

  const handleSave = (record) => {
    const fieldsError = form.getFieldsError();
    const hasErrors = fieldsError.some(({ errors }) => errors.length > 0);
    if (hasErrors) {
      return;
    }
    if (record.description) {
      setEditMode("");
      setInitialData({});
    } else {
      message.warning("Description required");
    }
  };

  const handleCancel = (key) => {
    const initialData = initialDataRef.current;
    if (initialData && initialData.description) {
      const newData = dataSource.map((item) =>
        item.key === initialData.key ? initialData : item
      );
      setDataSource(newData);
      form.setFieldsValue({
        items: newData,
      });
      setEditMode("");
      setInitialData({});
    } else {
      // message.warning("Initial record can't be canceled");
      const newDataSource = dataSource
        .filter((item) => item.key !== key)
        .map((item, index) => ({ ...item, key: index + 1 }));
      setDataSource(newDataSource);
      form.setFieldsValue({
        items: newDataSource,
      });
      setEditMode("");
      setInitialData({});
    }
  };
  const isEditing = (record) => record.key === editMode;

  const columns = [
    {
      title: "SNO.",
      dataIndex: "key",
      key: "key",
      align: "center",
    },
    {
      title: "ITEM DESCRIPTION",
      dataIndex: "description",
      key: "description",
      width: 250,
      render: (text, record, index) => (
        <Form.Item
          name={["items", index, "description"]}
          style={{ margin: 0 }}
          rules={[{ required: true, message: "Description is required" }]}
          initialValue={text}
        >
          <AutoCompleteDebounce
            options={itemOptions}
            onSelect={(value, option) => handleItemSelect(index, value, option)}
            onSearch={(value) => fetchItemOptions(value, index)}
            placeholder="Type to search"
            loading={loading[index]}
            disabled={!isEditing(record)}
            style={{ width: 250 }}
          />
        </Form.Item>
      ),
    },
    !hideColumns.hsn && {
      title: "HSN/SAC",
      dataIndex: "hsn",
      key: "hsn",
      width: 100,
      render: (text, record, index) => (
        <Form.Item
          name={["items", index, "hsn"]}
          style={{ margin: 0 }}
          // rules={[{ required: true, message: "HSN/SAC is required" }]}
          initialValue={text}
        >
          <Input disabled={!isEditing(record)} style={{ width: 100 }} />
        </Form.Item>
      ),
    },
    !hideColumns.quantity && {
      title: "QUANTITY",
      dataIndex: "quantity",
      key: "quantity",
      render: (text, record, index) => (
        <Form.Item
          name={["items", index, "quantity"]}
          style={{ margin: 0 }}
          initialValue={text}
        >
          <InputNumber min={0} disabled={!isEditing(record)} />
        </Form.Item>
      ),
    },
    {
      title: "UNIT MEASUREMENT",
      dataIndex: "unit_measurement",
      key: "unit_measurement",
      render: (text, record, index) => (
        <Form.Item
          name={["items", index, "unit_measurement"]}
          style={{ margin: 0 }}
          initialValue={text}
        >
          <Select style={{ width: 80 }} disabled={!isEditing(record)}>
            <Option value="mg">mg</Option>
            <Option value="g">g</Option>
            <Option value="kg">kg</Option>
            <Option value="cg">cg</Option>
            <Option value="lbs">lbs</Option>
            <Option value="oz">oz</Option>
            <Option value="t">ton</Option>
            <Option value="mL">mL</Option>
            <Option value="L">L</Option>
            <Option value="kL">kL</Option>
            <Option value="cm3">
              cm<sup>3</sup>
            </Option>
            <Option value="fl. oz">fl. oz</Option>
            <Option value="gal">gal</Option>
            <Option value="pack">pkt</Option>
            <Option value="pt">pt</Option>
          </Select>
        </Form.Item>
      ),
    },
    !hideColumns.unit_price && {
      title: "UNIT PRICE(₹)",
      dataIndex: "unit_price",
      key: "unit_price",
      render: (text, record, index) => (
        <Form.Item
          name={["items", index, "unit_price"]}
          style={{ margin: 0 }}
          initialValue={text}
        >
          <InputNumber min={0} disabled={!isEditing(record)} />
        </Form.Item>
      ),
    },
    {
      title: "PRICE(₹)",
      dataIndex: "price",
      key: "price",
      render: (text, record, index) => (
        <Form.Item
          name={["items", index, "price"]}
          style={{ margin: 0 }}
          initialValue={text}
        >
          <InputNumber
            min={0}
            disabled={!isEditing(record)}
            readOnly
            // ={selectedType==="vendor"}
          />
        </Form.Item>
      ),
    },
    {
      title: "DISCOUNT(%)",
      dataIndex: "discount",
      key: "discount",
      render: (text, record, index) => (
        <Form.Item
          name={["items", index, "discount"]}
          style={{ margin: 0 }}
          initialValue={text}
          rules={[
            {
              validator: (_, value) => {
                if (value < 0 || value > 100) {
                  return Promise.reject(new Error("0 to 100% only"));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <InputNumber disabled={!isEditing(record)} />
        </Form.Item>
      ),
    },
    {
      title: "AMOUNT(₹)",
      dataIndex: "amount",
      key: "amount",
      align: "center",
      width: 100,
      render: (text) => <span>{text}</span>,
    },
    {
      title: " ",
      key: "action",
      align: "center",
      width: 100,
      render: (record, index) => {
        const editable = isEditing(record);
        return (
          <>
            {editable ? (
              <Space style={{ gap: "20px" }}>
                <a onClick={() => handleSave(record)}>Save</a>
                <a onClick={() => handleCancel(record.key)}>Cancel</a>
              </Space>
            ) : (
              <Space style={{ gap: "20px" }}>
                <a
                  onClick={() => handleEdit(record)}
                  style={{ textDecoration: "underline" }}
                  disabled={editData?.is_generated || editMode}
                >
                  Edit
                </a>
                <a
                  onClick={() => handleDelete(record.key)}
                  style={{ textDecoration: "underline" }}
                  disabled={editData?.is_generated || editMode}
                >
                  Delete
                </a>
              </Space>
            )}
          </>
        );
      },
    },
  ].filter(Boolean);

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingBottom: "10px",
        }}
      >
        <Button
          onClick={handleAdd}
          disabled={
            editMode || editData?.is_generated
            // || !form.getFieldValue("name")
          }
          style={{ background: "#12005F", color: "white" }}
        >
          Add Item
        </Button>
        {(dataSource.length > 1 ||
          (dataSource.length == 1 && editMode != 1)) && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "8px",
              marginTop: "16px",
            }}
          >
            <Form.Item dependencies={["payment_status"]} noStyle>
              {({ getFieldValue }) => {
                const paymentStatus = getFieldValue("payment_status");
                if (
                  (paymentStatus !== "Paid" &&
                    paymentStatus !== "Pending" &&
                    paymentStatus !== "Partially paid") ||
                  editData?.is_generated
                ) {
                  return (
                    <Button
                      type="ghost"
                      onClick={() => handleSubmit("Save")}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                      loading={buttonLoad}
                      disabled={editMode}
                    >
                      <IconBillSave />
                      <div>Save</div>
                    </Button>
                  );
                }
                return null;
              }}
            </Form.Item>
            <Form.Item dependencies={["payment_status"]} noStyle>
              {({ getFieldValue }) => {
                const paymentStatus = getFieldValue("payment_status");
                if (paymentStatus !== "Draft" && !editData?.is_generated) {
                  return (
                    <Button
                      onClick={() => handleSubmit("Generate")}
                      type="ghost"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                      loading={buttonLoad}
                      disabled={editMode}
                    >
                      <IconGenerate />
                      <div>Generate</div>
                    </Button>
                  );
                }
                return null;
              }}
            </Form.Item>
            {editData?.receipt_link && (
              <Button
                key="receipt_link"
                type="ghost"
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
                loading={buttonLoad}
                disabled={editMode}
                href={editData?.receipt_link}
                target="_blank"
                rel="noopener noreferrer"
              >
                <IconPrint />
                <div>Print</div>
              </Button>
            )}
          </div>
        )}
      </div>
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        className="new-billing-table"
      />
    </>
  );
};

export default BillingTable;
